import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { gpsAPI, loadsAPI } from '../services/api';
import Button from './Button';
import {
  MapPinIcon,
  SignalIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  PauseIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const GPSActivation = ({ truckId, onLocationUpdate }) => {
  const { t } = useTranslation();
  const { user, getDriverTruck } = useAuth();
  const toast = useToast();
  
  const [isGPSActive, setIsGPSActive] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [watchId, setWatchId] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('prompt');
  const [accuracy, setAccuracy] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeLoad, setActiveLoad] = useState(null);
  const [availableLoads, setAvailableLoads] = useState([]);
  const [selectedLoadId, setSelectedLoadId] = useState('');
  const [loadingLoads, setLoadingLoads] = useState(false);
  const [trackingStats, setTrackingStats] = useState({
    totalUpdates: 0,
    startTime: null,
    distance: 0,
    avgSpeed: 0
  });
  const [locationHistory, setLocationHistory] = useState([]);

  const driverTruck = getDriverTruck();
  const effectiveTruckId = truckId || driverTruck?.id;

  // Check GPS permission status
  useEffect(() => {
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' })
        .then(result => {
          setPermissionStatus(result.state);
          result.addEventListener('change', () => {
            setPermissionStatus(result.state);
          });
        })
        .catch(() => {
          setPermissionStatus('unavailable');
        });
    }
  }, []);

  // Fetch available LOADED loads for the driver's truck
  useEffect(() => {
    const fetchAvailableLoads = async () => {
      if (!effectiveTruckId) {
        console.log('No truck ID, skipping load fetch');
        return;
      }

      console.log('Fetching available LOADED loads for truck:', effectiveTruckId);
      setLoadingLoads(true);
      try {
        const response = await loadsAPI.getAll({ 
          truckId: effectiveTruckId, 
          status: 'LOADED',
          limit: 50 
        });
        
        console.log('Loads response:', response.data);
        const loads = response.data.loads || [];
        setAvailableLoads(loads);
        
        // If there's only one LOADED load, auto-select it
        if (loads.length === 1) {
          const load = loads[0];
          console.log('Auto-selecting single load:', load);
          setSelectedLoadId(load.id);
          setActiveLoad({
            id: load.id,
            status: load.status,
            client: load.client?.companyName,
            product: load.product?.name,
            port: load.port?.name,
            weighbridge: load.weighbridge?.name,
            netWeight: load.netWeight
          });
        } else {
          console.log(`Found ${loads.length} LOADED loads for truck`);
        }
      } catch (error) {
        console.error('Error fetching loads:', error);
        const errorMessage = error.response?.data?.message || error.message || t('gps.errors.loadsFetchError');
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoadingLoads(false);
      }
    };

    fetchAvailableLoads();
  }, [effectiveTruckId, t, toast]);

  // GPS options for high accuracy tracking
  const gpsOptions = {
    enableHighAccuracy: true,
    timeout: 60000, // Increased to 60 seconds
    maximumAge: 30000 // Allow cached positions up to 30 seconds old
  };

  // Fallback GPS options with lower accuracy for better reliability
  const fallbackGpsOptions = {
    enableHighAccuracy: false,
    timeout: 45000, // 45 seconds for fallback
    maximumAge: 60000 // Allow older cached positions
  };

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in km
  };

  const handleLocationSuccess = async (position) => {
    console.log('GPS position received:', position);
    
    const { latitude, longitude, accuracy, altitude, heading, speed } = position.coords;
    const timestamp = new Date(position.timestamp);
    
    const locationData = {
      truckId: effectiveTruckId,
      loadId: selectedLoadId || '', // Ensure it's a string, not null/undefined
      latitude: Number(latitude),
      longitude: Number(longitude),
      altitude: altitude !== null && altitude !== undefined ? Number(altitude) : undefined,
      accuracy: accuracy !== null && accuracy !== undefined ? Number(accuracy) : undefined,
      heading: heading !== null && heading !== undefined && !isNaN(heading) ? Number(heading) : undefined,
      speed: speed !== null && speed !== undefined && !isNaN(speed) ? Math.max(0, Number(speed) * 3.6) : undefined,
      status: speed && speed > 0.5 ? 'IN_TRANSIT' : 'PARKED',
      batteryLevel: undefined, // Will be set below if available
      driverNotes: `Auto-updated by ${user.firstName} ${user.lastName} for Load ${activeLoad?.id || selectedLoadId || 'Unknown'}`
    };
    
    // Try to get battery level if available
    try {
      if (navigator.getBattery) {
        const battery = await navigator.getBattery();
        if (battery && typeof battery.level === 'number') {
          locationData.batteryLevel = Number(battery.level * 100);
        }
      }
    } catch (error) {
      console.log('Battery API not available or failed:', error);
    }

    console.log('Sending location data to server:', locationData);

    try {
      const response = await gpsAPI.updateLocation(locationData);
      console.log('GPS update response:', response.data);
      
      const newLocation = {
        ...locationData,
        timestamp
      };
      
      setCurrentLocation(newLocation);
      setAccuracy(accuracy);
      setLastUpdate(timestamp);
      setError(null);
      
      // Update location history
      setLocationHistory(prev => {
        const updated = [newLocation, ...prev.slice(0, 9)]; // Keep last 10 locations
        return updated;
      });
      
      // Update tracking statistics
      setTrackingStats(prev => {
        const newStats = {
          ...prev,
          totalUpdates: prev.totalUpdates + 1,
          startTime: prev.startTime || timestamp
        };
        
        // Calculate distance if we have previous location
        if (locationHistory.length > 0) {
          const lastLoc = locationHistory[0];
          const distance = calculateDistance(
            lastLoc.latitude, lastLoc.longitude,
            latitude, longitude
          );
          newStats.distance = prev.distance + distance;
        }
        
        // Calculate average speed
        if (newStats.startTime && newStats.distance > 0) {
          const timeElapsed = (timestamp - newStats.startTime) / (1000 * 60 * 60); // hours
          newStats.avgSpeed = newStats.distance / timeElapsed;
        }
        
        return newStats;
      });

      // Update active load information
      if (response.data.activeLoad) {
        setActiveLoad(response.data.activeLoad);
      }

      // Notify parent component
      if (onLocationUpdate) {
        onLocationUpdate(response.data.location);
      }

      // Show success toast only for manual updates or significant changes
      if (!isGPSActive || (accuracy && accuracy < 50)) {
        toast.success(t('gps.locationUpdated'));
      }
    } catch (error) {
      console.error('GPS update error:', error);
      const errorMessage = error.response?.data?.message || error.message || t('gps.updateError');
      console.error('Detailed GPS error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: errorMessage,
        errors: error.response?.data?.errors,
        dataTypes: error.response?.data?.dataTypes,
        receivedData: error.response?.data?.receivedData,
        fullResponse: error.response
      });
      
      // Show detailed validation errors if available
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const validationErrors = error.response.data.errors.map(err => err.msg).join(', ');
        setError(`${errorMessage}: ${validationErrors}`);
        toast.error(`${errorMessage}: ${validationErrors}`);
      } else {
        setError(errorMessage);
        toast.error(errorMessage);
      }
    }
  };

  const handleLocationError = (error, tryFallback = true) => {
    console.error('GPS location error:', error);
    
    let errorMessage;
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = t('gps.errors.permissionDenied');
        setPermissionStatus('denied');
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = t('gps.errors.positionUnavailable');
        break;
      case error.TIMEOUT:
        errorMessage = t('gps.errors.timeout');
        // Try fallback with lower accuracy if this is the first attempt
        if (tryFallback) {
          console.log('GPS timeout, trying fallback with lower accuracy...');
          toast.info(t('gps.tryingFallback'));
          navigator.geolocation.getCurrentPosition(
            handleLocationSuccess,
            (fallbackError) => handleLocationError(fallbackError, false), // Don't try fallback again
            fallbackGpsOptions
          );
          return; // Don't show error yet, wait for fallback
        }
        break;
      default:
        errorMessage = t('gps.errors.unknown');
        break;
    }
    
    console.error('GPS error details:', {
      code: error.code,
      message: error.message,
      translatedMessage: errorMessage
    });
    
    setError(errorMessage);
    toast.error(errorMessage);
    setIsLoading(false); // Reset loading state on error
  };

  const startGPSTracking = () => {
    console.log('Starting GPS tracking...', {
      hasGeolocation: !!navigator.geolocation,
      effectiveTruckId,
      selectedLoadId,
      permissionStatus,
      driverTruck
    });

    if (!navigator.geolocation) {
      const errorMsg = t('gps.errors.notSupported');
      console.error('GPS not supported:', errorMsg);
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    if (!effectiveTruckId) {
      const errorMsg = t('gps.errors.noTruckAssigned');
      console.error('No truck assigned:', errorMsg);
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    if (!selectedLoadId) {
      const errorMsg = t('gps.errors.noLoadSelected');
      console.error('No load selected:', errorMsg);
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    setIsLoading(true);
    setError(null);
    console.log('GPS tracking validation passed, starting...');

    try {
      // Get initial position
      navigator.geolocation.getCurrentPosition(
        handleLocationSuccess,
        (error) => {
          console.error('Initial position error:', error);
          handleLocationError(error);
          setIsLoading(false);
        },
        gpsOptions
      );

      // Start continuous tracking
      const id = navigator.geolocation.watchPosition(
        handleLocationSuccess,
        (error) => {
          // For continuous tracking, use fallback but don't stop tracking
          if (error.code === error.TIMEOUT) {
            console.log('GPS timeout during tracking, trying fallback...');
            toast.info(t('gps.tryingFallback'));
            navigator.geolocation.getCurrentPosition(
              handleLocationSuccess,
              (fallbackError) => console.error('Fallback also failed:', fallbackError),
              fallbackGpsOptions
            );
          } else {
            handleLocationError(error);
          }
        },
        gpsOptions
      );

      setWatchId(id);
      setIsGPSActive(true);
      setIsLoading(false);
      console.log('GPS tracking started successfully with watch ID:', id);
      toast.success(t('gps.trackingStarted'));
    } catch (error) {
      console.error('Error starting GPS tracking:', error);
      setError(t('gps.errors.unknown'));
      toast.error(t('gps.errors.unknown'));
      setIsLoading(false);
    }
  };

  const stopGPSTracking = () => {
    if (watchId) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
    
    setIsGPSActive(false);
    
    // Reset tracking statistics but keep location history
    setTrackingStats({
      totalUpdates: 0,
      startTime: null,
      distance: 0,
      avgSpeed: 0
    });
    
    toast.info(t('gps.trackingStopped'));
  };

  const updateLocationManually = () => {
    if (!navigator.geolocation) {
      setError(t('gps.errors.notSupported'));
      return;
    }

    if (!effectiveTruckId) {
      setError(t('gps.errors.noTruckAssigned'));
      return;
    }

    if (!selectedLoadId) {
      setError(t('gps.errors.noLoadSelected'));
      toast.error(t('gps.errors.noLoadSelected'));
      return;
    }

    setIsLoading(true);
    setError(null);
    
    // Try high accuracy first, with fallback on timeout
    navigator.geolocation.getCurrentPosition(
      (position) => {
        handleLocationSuccess(position);
        setIsLoading(false);
      },
      (error) => {
        handleLocationError(error);
        setIsLoading(false);
      },
      gpsOptions
    );
  };

  const getStatusIcon = () => {
    if (permissionStatus === 'denied') return XCircleIcon;
    if (isGPSActive) return CheckCircleIcon;
    if (error) return ExclamationTriangleIcon;
    return MapPinIcon;
  };

  const getStatusColor = () => {
    if (permissionStatus === 'denied') return 'text-red-500';
    if (isGPSActive) return 'text-green-500';
    if (error) return 'text-yellow-500';
    return 'text-gray-500';
  };

  const handleLoadSelection = (loadId) => {
    const selectedLoad = availableLoads.find(load => load.id === loadId);
    setSelectedLoadId(loadId);
    
    if (selectedLoad) {
      setActiveLoad({
        id: selectedLoad.id,
        status: selectedLoad.status,
        client: selectedLoad.client?.companyName,
        product: selectedLoad.product?.name,
        port: selectedLoad.port?.name,
        weighbridge: selectedLoad.weighbridge?.name,
        netWeight: selectedLoad.netWeight
      });
    } else {
      setActiveLoad(null);
    }
  };

  // Open current location in Google Maps
  const openCurrentLocationInMaps = () => {
    if (currentLocation) {
      const url = `https://www.google.com/maps?q=${currentLocation.latitude},${currentLocation.longitude}&z=15`;
      window.open(url, '_blank');
      toast.info(t('gps.openingGoogleMaps'));
    } else {
      toast.error(t('gps.noCurrentLocation'));
    }
  };

  // Open navigation to destination
  const openNavigationToDestination = () => {
    if (currentLocation && activeLoad?.destination) {
      const destination = `${activeLoad.destination.name}`;
      const url = `https://www.google.com/maps/dir/${currentLocation.latitude},${currentLocation.longitude}/${encodeURIComponent(destination)}`;
      window.open(url, '_blank');
      toast.info(t('gps.openingNavigation'));
    } else {
      toast.error(t('gps.noDestinationAvailable'));
    }
  };

  const StatusIcon = getStatusIcon();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <StatusIcon className={`h-6 w-6 ${getStatusColor()}`} />
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {t('gps.title')}
            </h3>
            <p className="text-sm text-gray-500">
              {effectiveTruckId ? 
                t('gps.truckAssigned', { plateNumber: driverTruck?.plateNumber || effectiveTruckId }) :
                t('gps.noTruckAssigned')
              }
            </p>
          </div>
        </div>

        <div className="flex space-x-2">
          {!isGPSActive ? (
            <Button
              onClick={startGPSTracking}
              disabled={!effectiveTruckId || !selectedLoadId || permissionStatus === 'denied' || isLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              <PlayIcon className="h-4 w-4 mr-2" />
              {t('gps.startTracking')}
            </Button>
          ) : (
            <Button
              onClick={stopGPSTracking}
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              <PauseIcon className="h-4 w-4 mr-2" />
              {t('gps.stopTracking')}
            </Button>
          )}
          
          <Button
            onClick={updateLocationManually}
            variant="outline"
            disabled={!effectiveTruckId || !selectedLoadId || isLoading}
          >
            <SignalIcon className="h-4 w-4 mr-2" />
            {t('gps.updateNow')}
          </Button>
        </div>
      </div>

      {/* Load Selection */}
      {availableLoads.length > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <div className="text-sm font-medium text-yellow-700 mb-2">{t('gps.selectLoadToTrack')}</div>
          <select
            value={selectedLoadId}
            onChange={(e) => handleLoadSelection(e.target.value)}
            className="w-full p-2 border border-yellow-300 rounded text-sm bg-white"
            disabled={isGPSActive}
          >
            <option value="">{t('gps.selectLoad')}</option>
            {availableLoads.map((load) => (
              <option key={load.id} value={load.id}>
                {load.client?.companyName} - {load.product?.name} → {load.port?.name}
                {load.netWeight && ` (${Math.round(load.netWeight)}kg)`}
              </option>
            ))}
          </select>
          {isGPSActive && (
            <p className="text-xs text-yellow-600 mt-1">{t('gps.cannotChangeLoadWhileTracking')}</p>
          )}
        </div>
      )}

      {/* No Loads Available */}
      {availableLoads.length === 0 && !loadingLoads && effectiveTruckId && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
            <div className="text-sm text-red-700">
              <strong>{t('gps.noLoadedLoads')}</strong>
              <p className="mt-1">{t('gps.noLoadedLoadsDescription')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Status Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-gray-50 p-3 rounded">
          <div className="text-sm font-medium text-gray-700">{t('gps.status')}</div>
          <div className={`text-sm ${getStatusColor()}`}>
            {permissionStatus === 'denied' ? t('gps.statuses.denied') :
             isGPSActive ? t('gps.statuses.active') :
             error ? t('gps.statuses.error') :
             t('gps.statuses.inactive')}
          </div>
        </div>

        {accuracy && (
          <div className="bg-gray-50 p-3 rounded">
            <div className="text-sm font-medium text-gray-700">{t('gps.accuracy')}</div>
            <div className={`text-sm ${accuracy < 10 ? 'text-green-600' : accuracy < 50 ? 'text-yellow-600' : 'text-red-600'}`}>
              ±{Math.round(accuracy)}m
            </div>
          </div>
        )}

        {lastUpdate && (
          <div className="bg-gray-50 p-3 rounded">
            <div className="text-sm font-medium text-gray-700">{t('gps.lastUpdate')}</div>
            <div className="text-sm text-gray-600">
              {lastUpdate.toLocaleTimeString()}
            </div>
          </div>
        )}
      </div>

      {/* Active Load Information */}
      {activeLoad && (
        <div className="bg-green-50 border border-green-200 p-3 rounded mb-4">
          <div className="text-sm font-medium text-green-700 mb-2">{t('gps.activeLoad')}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-600">{t('client')}:</span>
              <span className="ml-2 font-medium">{activeLoad.client}</span>
            </div>
            <div>
              <span className="text-gray-600">{t('product')}:</span>
              <span className="ml-2 font-medium">{activeLoad.product}</span>
            </div>
            <div>
              <span className="text-gray-600">{t('gps.destination')}:</span>
              <span className="ml-2 font-medium">{activeLoad.port}</span>
            </div>
            <div>
              <span className="text-gray-600">{t('status')}:</span>
              <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                activeLoad.status === 'LOADED' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {t(`loads.statuses.${activeLoad.status.toLowerCase()}`)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Real-time Tracking Information */}
      {isGPSActive && (
        <div className="bg-green-50 border border-green-200 p-4 rounded mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium text-green-800">{t('gps.liveTracking')}</h3>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-green-600">{t('gps.active')}</span>
            </div>
          </div>
          
          {/* Tracking Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className="text-center">
              <div className="text-lg font-bold text-green-700">{trackingStats.totalUpdates}</div>
              <div className="text-xs text-green-600">{t('gps.updates')}</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-700">
                {trackingStats.distance > 0 ? `${trackingStats.distance.toFixed(1)} km` : '0 km'}
              </div>
              <div className="text-xs text-green-600">{t('gps.distance')}</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-700">
                {trackingStats.avgSpeed > 0 ? `${Math.round(trackingStats.avgSpeed)} km/h` : '0 km/h'}
              </div>
              <div className="text-xs text-green-600">{t('gps.avgSpeed')}</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-700">
                {trackingStats.startTime ? 
                  Math.round((new Date() - trackingStats.startTime) / (1000 * 60)) : 0}min
              </div>
              <div className="text-xs text-green-600">{t('gps.duration')}</div>
            </div>
          </div>
        </div>
      )}

      {/* Current Location with Google Maps Actions */}
      {currentLocation && (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium text-blue-700">{t('gps.currentLocation')}</h3>
            <div className="flex space-x-2">
              <Button
                onClick={openCurrentLocationInMaps}
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-100 text-xs px-3 py-1"
              >
                <MapPinIcon className="h-4 w-4 mr-1" />
                {t('gps.viewOnMaps')}
              </Button>
              {activeLoad?.destination && (
                <Button
                  onClick={openNavigationToDestination}
                  variant="outline"
                  className="border-green-300 text-green-700 hover:bg-green-100 text-xs px-3 py-1"
                >
                  <PlayIcon className="h-4 w-4 mr-1" />
                  {t('gps.navigate')}
                </Button>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">{t('gps.latitude')}:</span>
              <span className="ml-2 font-mono text-blue-800">{currentLocation.latitude.toFixed(6)}</span>
            </div>
            <div>
              <span className="text-gray-600">{t('gps.longitude')}:</span>
              <span className="ml-2 font-mono text-blue-800">{currentLocation.longitude.toFixed(6)}</span>
            </div>
            {currentLocation.speed !== undefined && (
              <div>
                <span className="text-gray-600">{t('gps.speed')}:</span>
                <span className="ml-2 font-medium text-blue-800">
                  {currentLocation.speed ? `${Math.round(currentLocation.speed)} km/h` : '0 km/h'}
                </span>
              </div>
            )}
            {currentLocation.heading !== undefined && (
              <div>
                <span className="text-gray-600">{t('gps.heading')}:</span>
                <span className="ml-2 font-medium text-blue-800">
                  {currentLocation.heading ? `${Math.round(currentLocation.heading)}°` : 'N/A'}
                </span>
              </div>
            )}
            {accuracy && (
              <div>
                <span className="text-gray-600">{t('gps.accuracy')}:</span>
                <span className={`ml-2 font-medium ${
                  accuracy < 10 ? 'text-green-600' : 
                  accuracy < 50 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  ±{Math.round(accuracy)}m
                </span>
              </div>
            )}
            <div>
              <span className="text-gray-600">{t('gps.lastUpdate')}:</span>
              <span className="ml-2 text-blue-800">
                {lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Permission Denied Help */}
      {permissionStatus === 'denied' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-4">
          <div className="text-sm text-yellow-700">
            <strong>{t('gps.permissionDeniedHelp.title')}</strong>
            <ul className="mt-2 list-disc list-inside space-y-1">
              <li>{t('gps.permissionDeniedHelp.step1')}</li>
              <li>{t('gps.permissionDeniedHelp.step2')}</li>
              <li>{t('gps.permissionDeniedHelp.step3')}</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default GPSActivation;
